import { redirect } from '@tanstack/react-router';

import type { Permission, Role } from '@/lib/auth/permissions';
import type { SessionState } from '@/lib/auth/session';
import { abilitiesOf, isAuthenticated } from '@/lib/auth/session';

/**
 * Route guards for the TanStack Router trees (BP-01 §6 "Shared `lib/`: …
 * `router` (TanStack)"), split in two on purpose:
 *
 * * **Not signed in** is a navigation problem — `requireSession` throws
 *   TanStack Router's `redirect()` from `beforeLoad`, carrying the target in
 *   `?redirect=` so signing in lands the user where they were going.
 * * **Signed in but not allowed** is a rendering problem — redirecting would
 *   loop or lie about where the user is, so `checkAccess` reports `forbidden`
 *   and the `Gated` component renders a notice in place of the screen.
 *
 * The api enforces both independently (R8's `require()` dependencies); these
 * guards only decide what the browser bothers to draw.
 */

/** Where an unauthenticated caller is sent. */
export const LOGIN_PATH = '/login';

/**
 * Where a signed-in caller lands when no target was preserved (§11).
 *
 * BP-01 sent them to the first settings screen because there was nothing
 * else; BP-09 §6 makes `/` the home screen — §4 rule 7's cards — and every
 * staff role may see it, which `/settings/tenant` never was.
 */
export const DEFAULT_LANDING = '/';

/** Every role that is not `client` — BP-01 §6's "any staff" gate. */
export const STAFF_ROLES: readonly Role[] = [
  'owner',
  'manager',
  'frontdesk',
  'technician',
  'accountant',
];

/** What a route demands. Both conditions must hold when both are given. */
export interface Gate {
  roles?: readonly Role[];
  permission?: Permission;
}

/** The three answers a gate can give. */
export type Access = 'ok' | 'anonymous' | 'forbidden';

/** Decide whether `state` may see a route guarded by `gate`. */
export function checkAccess(state: SessionState, gate: Gate): Access {
  if (!isAuthenticated(state)) {
    return 'anonymous';
  }
  const ability = abilitiesOf(state);
  if (gate.roles !== undefined && !ability.isAny(...gate.roles)) {
    return 'forbidden';
  }
  if (gate.permission !== undefined && !ability.can(gate.permission)) {
    return 'forbidden';
  }
  return 'ok';
}

/** The search params `/login` understands. */
export interface LoginSearch {
  /** Where to go once signed in. */
  redirect: string | undefined;
}

/** `validateSearch` for the login route: keep a string `redirect`, drop anything else. */
export function parseLoginSearch(search: Record<string, unknown>): LoginSearch {
  const target = search['redirect'];
  return { redirect: typeof target === 'string' ? target : undefined };
}

/**
 * Where to go after a successful sign-in.
 *
 * Only a same-origin, non-protocol-relative path is honoured, and never the
 * login page itself — otherwise `?redirect=https://elsewhere.example` would turn
 * the login form into an open redirect.
 */
export function safeRedirect(target: string | undefined): string {
  if (target === undefined) {
    return DEFAULT_LANDING;
  }
  if (!target.startsWith('/') || target.startsWith('//')) {
    return DEFAULT_LANDING;
  }
  if (target.startsWith(LOGIN_PATH)) {
    return DEFAULT_LANDING;
  }
  return target;
}

/**
 * `beforeLoad` guard: send an unauthenticated caller to `/login`, preserving
 * `href` as the target.
 *
 * Throws TanStack Router's `redirect()` — the router unwinds it into a
 * navigation, so this never returns for an anonymous caller.
 */
export function requireSession(state: SessionState, href: string): void {
  if (!isAuthenticated(state)) {
    // TanStack Router's `redirect()` returns a plain control-flow object, not an
    // `Error`; throwing it is the documented way to redirect from `beforeLoad`.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: LOGIN_PATH, search: { redirect: href } });
  }
}
